// @ts-strict-ignore
import {Component, Input, OnChanges, OnInit, output, SimpleChanges} from "@angular/core";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {FontAwesomeCode, RoomCapability, TrainingRoom, TrainingRoomGroup, TrainingRoomType} from "@models";
import {SimEnumExtendedService, SimMaterialDialogService, SimTrainingRoomService, SimUtilityService} from "@services";
import {Translations} from "@translations";
import {TranslateService} from "@ngx-translate/core";

@Component({
	selector: "sim-training-room-button",
	templateUrl: "./training-room-button.component.html",
	styleUrls: ["./training-room-button.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class TrainingRoomButtonComponent implements OnInit, OnChanges {
	Utils = SimUtilityService;
	T = Translations;
	@Input() isChecked = false;
	@Input() trainingRoomGroup: TrainingRoomGroup;
	@Input() buttonState: "normal" | "occupied" | "debriefing" | "unavailable" | "selected" = "normal";
	@Input() isButtonView = true;
	onTrainingRoomClicked = output<{
		trainingRoom: TrainingRoom;
		checked: boolean;
	}>();
	@Input() trainingRoom: TrainingRoom;
	@Input() trainingRoomId: string | undefined;
	@Input() fullWidth: boolean = false;
	@Input() draggable = false;
	@Input() displayOnly = false;
	@Input() allowSelectionWhenOccupied = false;
	@Input() dragging = false;
	@Input() disabled = false;

	RoomCapability = RoomCapability;
	TrainingRoomType = TrainingRoomType;

	enableShowRoom: boolean = false;

	occupiedByName = "";

	constructor(
		private _simTrainingRoomService: SimTrainingRoomService,
		private _simDialogService: SimMaterialDialogService,
		private _simEnumService: SimEnumExtendedService,
		private _translate: TranslateService,
	) {}

	ngOnInit() {
		if (this.trainingRoomId) {
			this._simTrainingRoomService.getTrainingRoomById(this.trainingRoomId).then((trainingRoom) => {
				this.trainingRoom = trainingRoom;
				this.updateRoomInfo();
			});
		}
		this.updateRoomInfo();
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (changes["buttonState"]) {
			this.occupiedByName = await this.getTooltipTextForBtn();
		}
	}

	private updateRoomInfo() {
		if (this.trainingRoom) {
			this.enableShowRoom =
				this.trainingRoom?.type !== TrainingRoomType.NonSimulation &&
				this._simEnumService.containsFlags(this.trainingRoom.roomCapabilities, RoomCapability.Training);
		}
	}

	checkButton() {
		if (
			(this.buttonState !== "occupied" || this.allowSelectionWhenOccupied) &&
			this.buttonState != "selected" &&
			this.buttonState !== "unavailable"
		) {
			if (!this.dragging || (this.dragging && !this.isChecked)) {
				if (!this.displayOnly) {
					if (!this.disabled) {
						this.onTrainingRoomClicked.emit({
							trainingRoom: this.trainingRoom,
							checked: this.isChecked,
						});
					}
				}
			}
		}
	}

	openShowRoomDetailDialog() {
		this._simDialogService.openShowRoomDetailDialog({
			trainingRoom: this.trainingRoom,
		});
	}

	async getTooltipTextForBtn(): Promise<string> {
		switch (this.buttonState) {
			case "normal":
				return this.trainingRoom.name;
			case "occupied":
				let occupiedByName = this.trainingRoom?.occupiedTrainingRoom?.occupiedByName;
				if (!occupiedByName) {
					occupiedByName = await this._simTrainingRoomService.getOccupiedNameById(this.trainingRoom.id);
				}
				return this._translate.instant(this.T.TRAINING_ROOM_OCCUPIED_BY, {
					recorderName: `'${occupiedByName}'`,
				});
			case "selected":
				return this.trainingRoom.name + " is selected by " + this.trainingRoom?.occupiedTrainingRoom?.occupiedByName;
			case "debriefing":
				return "Player(s) in Room are used for Debriefing";
			case "unavailable":
				return this.trainingRoom.name + " currently unavailable";
		}
	}

	getStartIcon(): FontAwesomeCode {
		switch (this.trainingRoom?.type) {
			case TrainingRoomType.Physical:
				return {
					fontSet: "fad",
					fontIcon: "fa-door-open",
					themeColor: this.isButtonView ? "white" : "accent",
				};
			case TrainingRoomType.Virtual:
				return {
					fontSet: "fas",
					fontIcon: "fa-object-group",
					themeColor: this.isButtonView ? "white" : "accent",
				};
			//TODO: Implement case for TTT if within trainingroomtype
			default:
				return {
					fontSet: "fad",
					fontIcon: "fa-door-open",
					themeColor: this.isButtonView ? "white" : "accent",
				};
		}
	}
}
